import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography
} from '@material-ui/core';
import React from 'react';
import {
  ArrowRight,
  BarChart3,
  ChevronDown,
  MousePointerClick,
  Package,
  Presentation,
  Sparkles,
  Tags,
  TrendingDown
} from 'lucide-react';
import { DemoButton } from '../components/DemoButton';
import { Headline } from '../components/Headline';
import { Kicker } from '../components/Kicker';
import { Reveal } from '../components/motion';
import { EarlyAccessBadge } from '../components/NewBadge';
import { SEO } from '../components/SEO';
import MainLayout from '../layouts/main';
import styled from '../styled';
import { COLORS } from '../themes/color';

const INK = '#0e1c2b';

/* ------------------------------ Hero ------------------------------ */

const HeroPanel = styled('section')`
  position: relative;
  overflow: hidden;
  margin: 96px 24px 0;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
  border-radius: 24px;
  background: radial-gradient(
      ellipse 80% 70% at 80% 0%,
      rgba(24, 144, 255, 0.22) 0%,
      transparent 55%
    ),
    radial-gradient(
      ellipse 60% 50% at 10% 100%,
      rgba(0, 80, 179, 0.2) 0%,
      transparent 55%
    ),
    ${INK};
  color: #fff;
  padding: 72px 48px 64px;

  @media (max-width: 800px) {
    margin-top: 72px;
    padding: 48px 24px 40px;
  }
`;

const HeroGrid = styled('div')`
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 48px;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 36px;
  }
`;

const HeroCopy = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const HeroBadgeRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`;

const HeroLabel = styled('span')`
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #94a3b8;
  line-height: 20px;
`;

const HeroDescription = styled('p')`
  font-size: 18px;
  line-height: 1.65;
  color: #cbd5e1;
  max-width: 560px;
  margin: 20px 0 32px;
`;

/* --------------------------- Chat surface -------------------------- */
/*
 * Shared "chat with an AI tool" visual language used in the hero mockup
 * and each workflow card below. Deliberately avoids terminal/monospace
 * styling: our users picture Claude or ChatGPT, not a command line.
 */

const ChatWindow = styled('div')<{ dark?: boolean }>`
  border-radius: 18px;
  overflow: hidden;
  background: ${(p) => (p.dark ? 'rgba(8, 17, 30, 0.55)' : '#fff')};
  border: 1px solid ${(p) => (p.dark ? 'rgba(255, 255, 255, 0.12)' : '#e2e8f0')};
  box-shadow: ${(p) =>
    p.dark
      ? '0 24px 60px rgba(0, 0, 0, 0.35)'
      : '0 12px 40px rgba(14, 28, 43, 0.08)'};
`;

const ChatHeader = styled('div')<{ dark?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid
    ${(p) => (p.dark ? 'rgba(255, 255, 255, 0.08)' : '#f1f5f9')};
`;

const ChatAvatar = styled('div')`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${COLORS.PRIMARY};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ClientAvatar = styled('div')`
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ClientLogo = styled('img')`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ChatHeaderName = styled('div')<{ dark?: boolean }>`
  font-size: 13px;
  font-weight: 700;
  color: ${(p) => (p.dark ? '#fff' : INK)};
`;

const ChatHeaderStatus = styled('div')<{ dark?: boolean }>`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => (p.dark ? '#94a3b8' : '#64748b')};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #52c41a;
    box-shadow: 0 0 6px rgba(82, 196, 26, 0.7);
  }
`;

const ChatBody = styled('div')`
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const UserBubbleRow = styled('div')`
  display: flex;
  justify-content: flex-end;
`;

const UserBubble = styled('div')`
  max-width: 88%;
  background: ${COLORS.PRIMARY};
  color: #fff;
  padding: 10px 14px;
  border-radius: 16px 16px 4px 16px;
  font-size: 14.5px;
  line-height: 1.5;
`;

const AssistantBubbleRow = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const AssistantBubble = styled('div')<{ dark?: boolean }>`
  max-width: 92%;
  background: ${(p) => (p.dark ? 'rgba(255, 255, 255, 0.06)' : '#f8fafc')};
  border: 1px solid
    ${(p) => (p.dark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0')};
  color: ${(p) => (p.dark ? '#e2e8f0' : '#334155')};
  padding: 12px 14px;
  border-radius: 4px 16px 16px 16px;
  font-size: 14.5px;
  line-height: 1.6;
`;

const ReportChip = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.PRIMARY_LIGHT};
`;

/* ---------------------------- Sections ---------------------------- */

const Section = styled('section')`
  max-width: 1100px;
  margin: 0 auto;
  padding: 88px 24px 0;

  @media (max-width: 800px) {
    padding-top: 64px;
  }
`;

const SectionHeader = styled('div')`
  text-align: center;
  max-width: 720px;
  margin: 0 auto 48px;
`;

const SectionDescription = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #64748b;
  margin: 16px 0 0;
`;

/* --------------------------- Workflows ---------------------------- */

const WorkflowRow = styled('div')<{ flip?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 56px;
  align-items: center;
  padding: 40px 0;
  direction: ${(p) => (p.flip ? 'rtl' : 'ltr')};

  > * {
    direction: ltr;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 28px;
    direction: ltr;
    padding: 28px 0;
  }
`;

const WorkflowContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: flex-start;
`;

const WorkflowKickerRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const WorkflowIconBadge = styled('div')`
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #e6f7ff;
  color: ${COLORS.PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WorkflowKicker = styled('div')`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${COLORS.PRIMARY};
`;

const WorkflowBody = styled('p')`
  font-size: 16px;
  line-height: 1.65;
  color: #4f4f4f;
  margin: 0;
  max-width: 480px;
`;

const LayerChips = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const LayerChip = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.PRIMARY};
  background: #e6f7ff;
  border: 1px solid #bae7ff;
  border-radius: 100px;
  padding: 4px 12px;
`;

/* ------------------------ Assistant attachments -------------------- */

const AttachmentCard = styled('div')`
  margin-top: 10px;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e2e8f0;
`;

const AttachmentHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  background: #f1f5f9;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
`;

const SlideMockBody = styled('div')`
  padding: 16px;
`;

const SlideMockTitle = styled('div')`
  font-size: 13px;
  font-weight: 700;
  color: ${INK};
  margin-bottom: 12px;
`;

const SlideMockBars = styled('div')`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  height: 56px;
  padding: 0 2px;
`;

const SlideMockBar = styled('div')`
  flex: 1;
  border-radius: 4px 4px 0 0;
  background: linear-gradient(
    180deg,
    ${COLORS.PRIMARY_LIGHT} 0%,
    ${COLORS.PRIMARY} 100%
  );
`;

const SlideMockCaption = styled('div')`
  margin-top: 10px;
  font-size: 11.5px;
  color: #94a3b8;
`;

const RateChartBody = styled('div')`
  padding: 16px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 28px;
  height: 96px;
`;

const RateChartColumn = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  height: 100%;
  justify-content: flex-end;
`;

const RateChartBar = styled('div')<{ height: number; muted?: boolean }>`
  width: 34px;
  height: ${(p) => p.height}%;
  border-radius: 4px 4px 0 0;
  background: ${(p) => (p.muted ? '#f59e0b' : COLORS.PRIMARY)};
`;

const RateChartLabel = styled('div')`
  font-size: 11px;
  color: #64748b;
  text-align: center;
  line-height: 1.4;

  strong {
    display: block;
    font-size: 12.5px;
    color: ${INK};
  }
`;

/* -------------------------- More prompts -------------------------- */

const PromptChipGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  max-width: 860px;
  margin: 0 auto;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const PromptChip = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  line-height: 1.5;
  color: #334155;
  padding: 12px 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(14, 28, 43, 0.04);
`;

const PromptChipAvatar = styled('div')`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #e6f7ff;
  color: ${COLORS.PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* -------------------------- Data layers --------------------------- */

const DataLayerGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const DataLayerCard = styled('div')`
  padding: 22px 20px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(14, 28, 43, 0.04);
`;

const DataLayerIcon = styled('div')`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #e6f7ff;
  color: ${COLORS.PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
`;

const DataLayerTitle = styled('h3')`
  font-size: 16px;
  font-weight: 700;
  color: ${INK};
  margin: 0 0 8px;
`;

const DataLayerBody = styled('p')`
  font-size: 14px;
  line-height: 1.6;
  color: #64748b;
  margin: 0;
`;

/* ----------------------------- Clients ---------------------------- */

const ClientMarquee = styled('div')`
  overflow: hidden;
  mask-image: linear-gradient(
    to right,
    transparent,
    black 8%,
    black 92%,
    transparent
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    black 8%,
    black 92%,
    transparent
  );

  @keyframes mcpClientMarquee {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(-50%);
    }
  }
`;

const ClientTrack = styled('div')`
  display: flex;
  gap: 12px;
  width: fit-content;
  animation: mcpClientMarquee 40s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
  }
`;

const ClientChip = styled('span')`
  font-size: 14px;
  font-weight: 600;
  color: ${INK};
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 100px;
  padding: 10px 18px;
  white-space: nowrap;
  box-shadow: 0 4px 16px rgba(14, 28, 43, 0.04);
`;

/* ------------------------------ Steps ----------------------------- */

const StepsGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StepCard = styled('div')`
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 28px 24px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(14, 28, 43, 0.04);
`;

const StepNumber = styled('div')`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${COLORS.PRIMARY};
  margin-bottom: 12px;
`;

const StepTitle = styled('h3')`
  font-size: 20px;
  font-weight: 700;
  color: ${INK};
  margin: 0 0 10px;
`;

const StepBody = styled('p')`
  font-size: 15px;
  line-height: 1.65;
  color: #64748b;
  margin: 0;
`;

/* ------------------------------- FAQ ------------------------------ */

const FaqSection = styled('div')`
  max-width: 760px;
  margin: 0 auto;
`;

const FaqQuestion = styled('h3')`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
`;

const FinalCta = styled('div')`
  text-align: center;
  max-width: 720px;
  margin: 88px auto 120px;
  padding: 0 24px;
`;

/* ------------------------------ Data ------------------------------ */

const MCP_CLIENTS = [
  'Claude',
  'Codex',
  'ChatGPT',
  'Cursor',
  'Copilot',
  'Gemini',
  'VS Code',
  'Windsurf'
];

const workflows = [
  {
    icon: <Presentation size={16} />,
    client: { name: 'Claude', logo: '/images/logos/claude-mcp.svg' },
    kicker: 'Report and renew',
    title: 'Prepare a campaign wrap report for a client',
    body:
      'Campaign, placement, and page performance live in the same system as your commissions. Ask for the wrap-up and get placement-level results ready for the client deck, with a link to the live report behind the numbers.',
    prompt: 'Prepare a wrap report for the Bose spring campaign.',
    layers: ['Campaigns', 'Page performance', 'Commissions'],
    response:
      'Your Bose spring campaign wrapped with 12 placements and 480K pageviews, delivering $38.2K in attributed revenue. Your top placement was /best-noise-cancelling-headphones. Here is a slide with the full breakdown.',
    attachment: {
      type: 'slide' as const,
      fileName: 'Bose_Spring_Wrap.pptx',
      title: 'Bose Spring Wrap',
      caption: 'Weekly attributed revenue'
    },
    reportLabel: 'Open the full campaign report'
  },
  {
    icon: <Package size={16} />,
    client: { name: 'Copilot', logo: '/images/logos/copilot-mcp.svg' },
    kicker: 'Protect revenue',
    title: 'Find out-of-stock products on your top pages',
    body:
      'Affilimate matches the product links on your pages against a catalog of 50M+ products. Ask once and get the pages losing revenue right now, plus in-stock replacements you could swap in.',
    prompt: 'Which of our top pages link to out-of-stock products?',
    layers: ['Product catalog', 'Page performance', 'Commissions'],
    response:
      '3 of your highest-traffic pages link to products that are now out of stock, putting about 41K pageviews at risk. /best-espresso-machines has the most affected links. I found 9 in-stock replacements in your catalog at similar commission rates.',
    attachment: null,
    reportLabel: 'Open the content report'
  },
  {
    icon: <TrendingDown size={16} />,
    client: { name: 'ChatGPT', logo: '/images/logos/chatgpt-mcp.svg' },
    kicker: 'Plan and negotiate',
    title: 'Model a rate change before it hits your revenue',
    body:
      'Commission rates and transactions from 100+ networks sit next to your page data. Ask what a rate cut would cost based on what you actually earned, and see which pages carry the exposure.',
    prompt:
      'Forecast our revenue if our top merchant cuts commission rates from 10% to 8%.',
    layers: ['Commissions', 'Rates', 'Page performance'],
    response:
      'If your top merchant cuts rates from 10% to 8%, you would lose about $12.4K a month based on your last 90 days of transactions. That is concentrated in just 14 pages, and 3 alternate merchants carry the same products at 9 to 12%.',
    attachment: {
      type: 'chart' as const,
      bars: [
        { label: 'Current', value: '$62.0K/mo', height: 100 },
        { label: 'At 8%', value: '$49.6K/mo', height: 80, muted: true }
      ]
    },
    reportLabel: 'Open the rates report'
  }
];

const morePrompts = [
  'Break down affiliate earnings by author this quarter and flag sections in decline.',
  'Which product pages had high clicks but declining commissions this month?',
  'Compare commission rates for our top outdoor gear advertisers.',
  'Summarize earnings by network for the last 30 days and flag anything down more than 10%.',
  'Which of our pages linked to Wayfair converted best in Q2?',
  'Which authors drove the most revenue growth this quarter?'
];

const dataLayers = [
  {
    icon: <BarChart3 size={20} />,
    title: 'Commissions and finance',
    body:
      'Transactions, rates, payouts, and earned versus paid status from 100+ affiliate networks, kept in one place instead of exports.'
  },
  {
    icon: <MousePointerClick size={20} />,
    title: 'Page performance',
    body:
      'Clicks, pageviews, and revenue at the page and link level. See what readers actually do on the URLs you monetize.'
  },
  {
    icon: <Package size={20} />,
    title: 'Product catalog',
    body:
      '50M+ products matched to your links. Spot out-of-stock items, find in-stock replacements, and scope swap opportunities.'
  },
  {
    icon: <Tags size={20} />,
    title: 'Custom dimensions',
    body:
      'Author, section, campaign, send ID, syndication partner: your editorial and channel taxonomy, queryable alongside revenue.'
  }
];

const faqs = [
  {
    question: 'What is the Affilimate MCP server?',
    answer:
      'A secure connection between your AI tools and your Affilimate workspace. Your commissions, page performance, catalog-matched products, and custom dimensions live in one system here, so one question can cut across all of them.'
  },
  {
    question: 'What can I do with it?',
    answer:
      'Ask questions that need the full picture: find out-of-stock products on your top pages, prepare a campaign wrap report, model what a rate change would cost, compare author or section performance, and check merchant rates. Answers link back to live reports when you need to verify the numbers.'
  },
  {
    question: 'Which AI tools does it work with?',
    answer:
      'We test it most extensively with Claude and Codex. MCP is an open standard, so it also works with hundreds of other compatible tools, including ChatGPT, Cursor, Gemini, VS Code, Windsurf, and Copilot. If yours supports remote MCP with OAuth, it is worth trying.'
  },
  {
    question: 'How do I set it up?',
    answer:
      'Sign in to Affilimate, choose one workspace, and approve access. Most teams connect in under five minutes, no engineering work required.'
  },
  {
    question: 'What do I need to get started?',
    answer:
      'An Affilimate account with MCP enabled for your workspace and an AI client that supports MCP. There are no API keys to manage.'
  }
];

export default () => (
  <MainLayout>
    <SEO
      pathname="/mcp/"
      title="Affilimate MCP: Affiliate Data for Claude, ChatGPT & AI Tools"
      description="Bring your affiliate data into Claude, ChatGPT, and Cursor with Affilimate MCP. Find out-of-stock products, prepare campaign wrap reports, and model commission rate changes."
    />

    <HeroPanel>
      <HeroGrid>
        <HeroCopy>
          <HeroBadgeRow>
            <HeroLabel>MCP</HeroLabel>
            <EarlyAccessBadge />
          </HeroBadgeRow>
          <Headline component="h1" variant="h1" style={{ color: '#fff' }}>
            Put your commerce data to work in any AI tool
          </Headline>
          <HeroDescription>
            Affilimate MCP connects Claude, ChatGPT, Cursor, and the rest of
            your stack to your affiliate data: commissions, page performance,
            catalog-matched products, and custom dimensions. Ask real
            questions, get answers from your own history, and jump straight
            to the report behind them.
          </HeroDescription>
          <DemoButton text="Get a demo" variant="contained" />
        </HeroCopy>

        <Reveal delay={120}>
          <ChatWindow dark aria-hidden>
            <ChatHeader dark>
              <ClientAvatar>
                <ClientLogo src="/images/logos/claude-mcp.svg" alt="" />
              </ClientAvatar>
              <ChatHeaderName dark>Claude</ChatHeaderName>
              <ChatHeaderStatus dark>Connected</ChatHeaderStatus>
            </ChatHeader>
            <ChatBody>
              <UserBubbleRow>
                <UserBubble>
                  Which of our top pages have out-of-stock products?
                </UserBubble>
              </UserBubbleRow>
              <AssistantBubbleRow>
                <ClientAvatar>
                  <ClientLogo src="/images/logos/claude-mcp.svg" alt="" />
                </ClientAvatar>
                <AssistantBubble dark>
                  3 top-traffic pages link to out-of-stock machines. 41K
                  pageviews are at risk on{' '}
                  <strong style={{ color: '#fff' }}>
                    /best-espresso-machines
                  </strong>
                  , and I found 9 in-stock replacements in your catalog.
                  <br />
                  <ReportChip>
                    View content report <ArrowRight size={12} />
                  </ReportChip>
                </AssistantBubble>
              </AssistantBubbleRow>
            </ChatBody>
          </ChatWindow>
        </Reveal>
      </HeroGrid>
    </HeroPanel>

    <Section>
      <SectionHeader>
        <Reveal>
          <Kicker>What you can do</Kicker>
          <Headline component="h2">
            Don&apos;t just chat: workflows your team can hand to an AI tool
          </Headline>
          <SectionDescription>
            These are real workflows, not chat. Each one crosses data that
            normally lives in separate systems, and each one ends in
            something you can act on.
          </SectionDescription>
        </Reveal>
      </SectionHeader>

      {workflows.map((workflow, index) => (
        <WorkflowRow key={workflow.title} flip={index % 2 === 1}>
          <Reveal>
            <WorkflowContent>
              <WorkflowKickerRow>
                <WorkflowIconBadge>{workflow.icon}</WorkflowIconBadge>
                <WorkflowKicker>{workflow.kicker}</WorkflowKicker>
              </WorkflowKickerRow>
              <Headline component="h3" variant="h4" style={{ margin: 0 }}>
                {workflow.title}
              </Headline>
              <WorkflowBody>{workflow.body}</WorkflowBody>
              <LayerChips>
                {workflow.layers.map((layer) => (
                  <LayerChip key={layer}>{layer}</LayerChip>
                ))}
              </LayerChips>
            </WorkflowContent>
          </Reveal>
          <Reveal delay={120}>
            <ChatWindow aria-hidden>
              <ChatHeader>
                <ClientAvatar>
                  <ClientLogo src={workflow.client.logo} alt="" />
                </ClientAvatar>
                <ChatHeaderName>{workflow.client.name}</ChatHeaderName>
                <ChatHeaderStatus>Connected</ChatHeaderStatus>
              </ChatHeader>
              <ChatBody>
                <UserBubbleRow>
                  <UserBubble>{workflow.prompt}</UserBubble>
                </UserBubbleRow>
                <AssistantBubbleRow>
                  <ClientAvatar>
                    <ClientLogo src={workflow.client.logo} alt="" />
                  </ClientAvatar>
                  <AssistantBubble>
                    {workflow.response}
                    {workflow.attachment?.type === 'slide' && (
                      <AttachmentCard>
                        <AttachmentHeader>
                          <Presentation size={13} />
                          {workflow.attachment.fileName}
                        </AttachmentHeader>
                        <SlideMockBody>
                          <SlideMockTitle>
                            {workflow.attachment.title}
                          </SlideMockTitle>
                          <SlideMockBars>
                            <SlideMockBar style={{ height: '45%' }} />
                            <SlideMockBar style={{ height: '70%' }} />
                            <SlideMockBar style={{ height: '90%' }} />
                            <SlideMockBar style={{ height: '60%' }} />
                            <SlideMockBar style={{ height: '80%' }} />
                          </SlideMockBars>
                          <SlideMockCaption>
                            {workflow.attachment.caption}
                          </SlideMockCaption>
                        </SlideMockBody>
                      </AttachmentCard>
                    )}
                    {workflow.attachment?.type === 'chart' && (
                      <AttachmentCard>
                        <AttachmentHeader>
                          <TrendingDown size={13} />
                          Revenue impact
                        </AttachmentHeader>
                        <RateChartBody>
                          {workflow.attachment.bars.map((bar) => (
                            <RateChartColumn key={bar.label}>
                              <RateChartBar
                                height={bar.height}
                                muted={bar.muted}
                              />
                              <RateChartLabel>
                                {bar.label}
                                <strong>{bar.value}</strong>
                              </RateChartLabel>
                            </RateChartColumn>
                          ))}
                        </RateChartBody>
                      </AttachmentCard>
                    )}
                    <br />
                    <ReportChip style={{ color: COLORS.PRIMARY }}>
                      {workflow.reportLabel} <ArrowRight size={12} />
                    </ReportChip>
                  </AssistantBubble>
                </AssistantBubbleRow>
              </ChatBody>
            </ChatWindow>
          </Reveal>
        </WorkflowRow>
      ))}

      <Reveal delay={80}>
        <SectionHeader style={{ marginTop: 48, marginBottom: 28 }}>
          <Typography
            variant="h6"
            component="h3"
            style={{ fontWeight: 700, color: INK }}
          >
            More things teams ask
          </Typography>
        </SectionHeader>
        <PromptChipGrid>
          {morePrompts.map((prompt) => (
            <PromptChip key={prompt}>
              <PromptChipAvatar>
                <Sparkles size={12} />
              </PromptChipAvatar>
              {prompt}
            </PromptChip>
          ))}
        </PromptChipGrid>
      </Reveal>
    </Section>

    <Section>
      <SectionHeader>
        <Reveal>
          <Kicker>Why it works</Kicker>
          <Headline component="h2">Four kinds of data. One system.</Headline>
          <SectionDescription>
            These four layers rarely live in the same place. Because they do
            here, one question can cross all of them without anyone building
            a custom report first.
          </SectionDescription>
        </Reveal>
      </SectionHeader>
      <DataLayerGrid>
        {dataLayers.map((layer, index) => (
          <Reveal key={layer.title} delay={index * 70}>
            <DataLayerCard>
              <DataLayerIcon>{layer.icon}</DataLayerIcon>
              <DataLayerTitle>{layer.title}</DataLayerTitle>
              <DataLayerBody>{layer.body}</DataLayerBody>
            </DataLayerCard>
          </Reveal>
        ))}
      </DataLayerGrid>
    </Section>

    <Section>
      <SectionHeader>
        <Reveal>
          <Kicker>Works where you work</Kicker>
          <Headline component="h2">
            One connection, all your AI tools
          </Headline>
          <SectionDescription>
            Connect once with OAuth. Any MCP-compatible client can reach the
            same data, governed by the same workspace permissions. We test it
            most extensively with Claude and Codex.
          </SectionDescription>
        </Reveal>
      </SectionHeader>
      <ClientMarquee aria-hidden>
        <ClientTrack>
          {[...MCP_CLIENTS, ...MCP_CLIENTS].map((client, index) => (
            <ClientChip key={`${client}-${index}`}>{client}</ClientChip>
          ))}
        </ClientTrack>
      </ClientMarquee>
    </Section>

    <Section>
      <SectionHeader>
        <Reveal>
          <Kicker>Setup</Kicker>
          <Headline component="h2">Connect in three steps</Headline>
        </Reveal>
      </SectionHeader>
      <StepsGrid>
        <Reveal delay={0} style={{ height: '100%' }}>
          <StepCard>
            <StepNumber>Step 1</StepNumber>
            <StepTitle>Connect</StepTitle>
            <StepBody>
              Add Affilimate as a connector in your AI tool of choice.
            </StepBody>
          </StepCard>
        </Reveal>
        <Reveal delay={80} style={{ height: '100%' }}>
          <StepCard>
            <StepNumber>Step 2</StepNumber>
            <StepTitle>Authorize</StepTitle>
            <StepBody>
              Sign in to Affilimate and choose the one workspace this
              connector can access. OAuth handles the rest. There are no API
              keys to copy.
            </StepBody>
          </StepCard>
        </Reveal>
        <Reveal delay={160} style={{ height: '100%' }}>
          <StepCard>
            <StepNumber>Step 3</StepNumber>
            <StepTitle>Put it to work</StepTitle>
            <StepBody>
              Start with a real task: a stock check, a wrap report, or a
              rate scenario. Answers link back to live Affilimate reports so
              your team can verify.
            </StepBody>
          </StepCard>
        </Reveal>
      </StepsGrid>
    </Section>

    <Section style={{ paddingTop: 96 }}>
      <SectionHeader>
        <Reveal>
          <Headline component="h2">FAQs</Headline>
          <SectionDescription>
            Quick answers to the most common questions about Affilimate MCP.
          </SectionDescription>
        </Reveal>
      </SectionHeader>
      <FaqSection>
        {faqs.map((faq, index) => (
          <Accordion key={faq.question} defaultExpanded={index === 0}>
            <AccordionSummary expandIcon={<ChevronDown size={20} />}>
              <FaqQuestion>{faq.question}</FaqQuestion>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" style={{ color: '#444' }}>
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </FaqSection>
    </Section>

    <FinalCta>
      <Reveal>
        <Headline component="h2">See it on your own data</Headline>
        <SectionDescription style={{ marginBottom: 28 }}>
          Book a demo and bring a real question: a stock check, a campaign
          wrap, or a rate scenario. We&apos;ll run it on data like yours.
        </SectionDescription>
        <DemoButton text="Get a demo" variant="contained" />
      </Reveal>
    </FinalCta>
  </MainLayout>
);
